import React, { useState, useEffect } from "react";
import {
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  Chip,
  IconButton,
  Button,
  CircularProgress,
  Alert,
  Stack,
  TextField,
  InputAdornment,
  Modal,
  Fade,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  TablePagination,
} from "@mui/material";
import {
  Business as BusinessIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  Search as SearchIcon,
  ToggleOff as ToggleOffIcon,
  ToggleOn as ToggleOnIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../services/api/axiosConfig";
import {
  normalizarNomeEmpresa,
  normalizarEmail,
  normalizarTelefone,
  normalizarCNPJ,
} from "../../utils/normalization";
import { getApiBaseUrl } from "../../services/baseUrl";

interface Empresa {
  id: number;
  nome: string;
  cnpj: string;
  email: string;
  telefone: string;
  imagem: string;
  ativo: string;
}

const ListarEmpresas: React.FC = () => {
  const navigate = useNavigate();
  const [empresas, setEmpresas] = useState<Empresa[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<
    "success" | "error" | "warning" | "info"
  >("info");
  
  // Estados para paginação
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Estados para modal de confirmação
  const [modalOpen, setModalOpen] = useState(false);
  const [empresaParaAlterar, setEmpresaParaAlterar] = useState<Empresa | null>(null);
  const [acaoModal, setAcaoModal] = useState<"ativar" | "inativar">("inativar");

  const showMessage = (
    message: string,
    severity: "success" | "error" | "warning" | "info",
  ) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  useEffect(() => {
    fetchEmpresas();

    // Listener para atualização de imagem de empresa (igual ao painel-vagas)
    const handleImagemAtualizada = (event: CustomEvent) => {
      fetchEmpresas();
    };

    window.addEventListener(
      "empresaImagemAtualizada",
      handleImagemAtualizada as EventListener,
    );

    return () => {
      window.removeEventListener(
        "empresaImagemAtualizada",
        handleImagemAtualizada as EventListener,
      );
    };
  }, []);

  const fetchEmpresas = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`${getApiBaseUrl()}/empresas`);
      if (response.data.success && response.data.data) {
        // Aplicar lógica de imagem do localStorage igual ao painel-vagas
        const empresasComImagens = response.data.data.map(
          (empresa: Empresa) => {
            const imagemLocalStorage = localStorage.getItem(
              `empresaImagem_${empresa.id}`,
            );
            return {
              ...empresa,
              imagem: imagemLocalStorage || "", // Apenas localStorage ou vazio
            };
          },
        );
        setEmpresas(empresasComImagens);
      }
    } catch (err) {
      setError("Erro ao carregar empresas");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const abrirModalConfirmacao = (empresa: Empresa) => {
    const novoStatus = empresa.ativo === "S" ? "N" : "S";
    const acao = novoStatus === "S" ? "ativar" : "inativar";
    
    setEmpresaParaAlterar(empresa);
    setAcaoModal(acao);
    setModalOpen(true);
  };

  const fecharModal = () => {
    setModalOpen(false);
    setEmpresaParaAlterar(null);
  };

  const confirmarAlteracao = async () => {
    if (!empresaParaAlterar) return;

    const empresa = empresaParaAlterar;
    const novoStatus = empresa.ativo === "S" ? "N" : "S";
    const acao = novoStatus === "S" ? "ativar" : "inativar";

    fecharModal();
    
    try {
        // Para inativar/ativar, enviar apenas campos essenciais para evitar problemas com dados corrompidos
        const dadosEssenciais: any = {
          id: empresa.id,
          nome: empresa.nome || "",
          cnpj: empresa.cnpj ? empresa.cnpj.replace(/\D/g, "") : "",
          email: empresa.email || "",
          telefone: empresa.telefone
            ? String(empresa.telefone).replace(/\D/g, "")
            : "",
          ativo: novoStatus,
        };

        // Adicionar campos opcionais apenas se existirem e forem válidos
        if (empresa.imagem && typeof empresa.imagem === "string") {
          dadosEssenciais.imagem = empresa.imagem;
        }

        // Validar dados essenciais
        if (!dadosEssenciais.nome || !dadosEssenciais.cnpj) {
          throw new Error(
            `Dados essenciais ausentes - Nome: "${dadosEssenciais.nome}", CNPJ: "${dadosEssenciais.cnpj}"`,
          );
        }

        if (dadosEssenciais.cnpj.length !== 14) {
          throw new Error(
            `CNPJ inválido: "${dadosEssenciais.cnpj}" (deve ter 14 dígitos)`,
          );
        }

        const dadosParaEnvio = dadosEssenciais;

        const response = await axiosInstance.put(
          `${getApiBaseUrl()}/empresas/${empresa.id}`,
          dadosParaEnvio,
          {
            timeout: 10000,
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
          },
        );

        if (response.data.success) {
          // Atualiza a lista local
          setEmpresas(
            empresas.map((emp) =>
              emp.id === empresa.id ? { ...emp, ativo: novoStatus } : emp,
            ),
          );

          // Notificar o painel de vagas para atualizar
          const event = new CustomEvent("empresaAtualizada", {
            detail: { empresa: { ...empresa, ativo: novoStatus } },
          });
          window.dispatchEvent(event);

          showMessage(
            `Empresa ${
              acao === "ativar" ? "ativada" : "inativada"
            } com sucesso!`,
            "success",
          );
        } else {
          console.error("API retornou success: false:", response.data);
          throw new Error(
            response.data.message || "API retornou sucesso false",
          );
        }
      } catch (err: any) {
        console.error(`Erro ao ${acao} empresa:`, {
          error: err,
          message: err?.message,
          status: err?.response?.status,
          data: err?.response?.data,
          empresaId: empresa.id,
          empresaNome: empresa.nome,
        });

        // Para erro 500 na inativação, assumir que é devido a vagas ativas (caso mais comum)
        const errorMessage = String(
          err?.response?.data?.message || err?.response?.data || "",
        );
        const isVagasAtivas =
          (acao === "inativar" && err?.response?.status === 500) ||
          errorMessage.toLowerCase().includes("vaga") ||
          errorMessage.toLowerCase().includes("ativa") ||
          errorMessage.toLowerCase().includes("constraint") ||
          errorMessage.toLowerCase().includes("foreign key") ||
          errorMessage.toLowerCase().includes("cannot delete") ||
          errorMessage.toLowerCase().includes("referenced") ||
          errorMessage.toLowerCase().includes("violates") ||
          errorMessage.toLowerCase().includes("integrity") ||
          errorMessage.toLowerCase().includes("dependent");

        // Tentar fallback apenas se NÃO for erro de vagas ativas e for erro 500
        if (err?.response?.status === 500 && !isVagasAtivas) {
          try {
            const dadosMinimos = {
              id: empresa.id,
              ativo: novoStatus,
            };

            const responseFallback = await axiosInstance.patch(
              `${getApiBaseUrl()}/empresas/${empresa.id}/status`,
              dadosMinimos,
              {
                timeout: 5000,
                headers: {
                  "Content-Type": "application/json",
                  Accept: "application/json",
                },
              },
            );

            if (responseFallback.data.success) {
              setEmpresas(
                empresas.map((emp) =>
                  emp.id === empresa.id ? { ...emp, ativo: novoStatus } : emp,
                ),
              );
              showMessage(
                `Empresa ${
                  acao === "ativar" ? "ativada" : "inativada"
                } com sucesso!`,
                "success",
              );
              return; // Sair da função sem mostrar erro
            }
          } catch (fallbackErr) {
            console.error("Fallback também falhou:", fallbackErr);
          }
        }

        let mensagemErro = `Erro ao ${acao} empresa`;

        if (err?.response?.status === 400) {
          mensagemErro = `Dados inválidos para ${acao} empresa. Verifique as informações.`;
        } else if (err?.response?.status === 404) {
          mensagemErro = `Empresa não encontrada. Atualize a página e tente novamente.`;
        } else if (err?.response?.status === 500) {
          console.error("Detalhes do erro 500:", {
            empresaId: empresa.id,
            empresaNome: empresa.nome,
            cnpj: empresa.cnpj,
            responseData: err?.response?.data,
          });

          console.error("Análise completa do erro 500:", {
            fullResponse: err?.response,
            data: err?.response?.data,
            message: err?.response?.data?.message,
            status: err?.response?.status,
            statusText: err?.response?.statusText,
          });

          // Verificar se o erro é relacionado a vagas ativas
          const errorData = err?.response?.data;
          const errorMessage = String(
            errorData?.message || errorData?.error || errorData || "",
          );

          const isVagasAtivas =
            errorMessage.toLowerCase().includes("vaga") ||
            errorMessage.toLowerCase().includes("ativa") ||
            errorMessage.toLowerCase().includes("constraint") ||
            errorMessage.toLowerCase().includes("foreign key") ||
            errorMessage.toLowerCase().includes("cannot delete") ||
            errorMessage.toLowerCase().includes("referenced") ||
            errorMessage.toLowerCase().includes("violates") ||
            errorMessage.toLowerCase().includes("integrity") ||
            errorMessage.toLowerCase().includes("dependent") ||
            (acao === "inativar" && err?.response?.status === 500); // Assumir vagas ativas para erro 500 em inativação

          if (isVagasAtivas && acao === "inativar") {
            mensagemErro = `Não foi possível inativar a empresa "${empresa.nome}" porque ela possui vagas de emprego ativas.\n\nPara inativar esta empresa, primeiro finalize todas as suas vagas ativas no painel de gestão.`;
          } else {
            mensagemErro = `Erro interno do servidor ao processar empresa "${empresa.nome}" (ID: ${empresa.id}). Verifique os dados da empresa ou tente novamente.`;
          }
        } else if (err?.message?.includes("timeout")) {
          mensagemErro = `Timeout ao ${acao} empresa. Verifique sua conexão e tente novamente.`;
        } else if (err?.response?.data?.message) {
          mensagemErro = `Erro: ${err.response.data.message}`;
        }

        showMessage(mensagemErro, "error");
      }
  };

  const filteredEmpresas = empresas
    .filter((empresa) => {
      const matchesSearch =
        normalizarNomeEmpresa(empresa.nome)
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        normalizarCNPJ(empresa.cnpj).includes(searchTerm) ||
        normalizarEmail(empresa.email)
          .toLowerCase()
          .includes(searchTerm.toLowerCase());

      const matchesStatus = showInactive
        ? empresa.ativo === "N"
        : empresa.ativo === "S";

      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => a.nome.localeCompare(b.nome));

  // Paginação
  const empresasPaginadas = filteredEmpresas.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (loading) {
    return (
      <Container
        maxWidth="lg"
        sx={{ mt: 4, display: "flex", justifyContent: "center" }}
      >
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4, px: { xs: 2, sm: 3 } }}>
      <Paper elevation={3} sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          mb={3}
          flexDirection={{ xs: "column", sm: "row" }}
          gap={{ xs: 2, sm: 0 }}
        >
          <Box
            display="flex"
            alignItems="center"
            sx={{ width: { xs: "100%", sm: "auto" } }}
          >
            <BusinessIcon
              sx={{
                mr: 2,
                fontSize: { xs: 32, sm: 40 },
                color: "primary.main",
              }}
            />
            <Typography
              variant="h4"
              component="h1"
              sx={{ fontSize: { xs: "1.5rem", sm: "2rem", md: "2.125rem" } }}
            >
              {showInactive ? "Empresas Inativas" : "Empresas Cadastradas"}
            </Typography>
          </Box>
          <Button
            variant={showInactive ? "contained" : "outlined"}
            startIcon={showInactive ? <ToggleOnIcon /> : <ToggleOffIcon />}
            onClick={() => setShowInactive(!showInactive)}
            sx={{
              width: { xs: "100%", sm: "auto" },
              minWidth: { sm: "140px" },
              bgcolor: showInactive ? "warning.main" : "transparent",
              color: showInactive ? "white" : "warning.main",
              borderColor: "warning.main",
              "&:hover": {
                bgcolor: showInactive
                  ? "warning.dark"
                  : "rgba(237, 108, 2, 0.04)",
                borderColor: "warning.dark",
                color: showInactive ? "white" : "warning.dark",
              },
            }}
          >
            {showInactive ? "Ver Ativas" : "Ver Inativas"}
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box mb={3}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Pesquisar por nome, CNPJ ou email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Tabela para desktop */}
        <Box sx={{ display: { xs: "none", md: "block" } }}>
          <TableContainer>
            <Table sx={{ minWidth: 900 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Nome</TableCell>
                  <TableCell sx={{ minWidth: 180, fontWeight: 'bold' }}>CNPJ/CPF</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Email</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Telefone</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>Status</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {empresasPaginadas.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      Nenhuma empresa encontrada
                    </TableCell>
                  </TableRow>
                ) : (
                  empresasPaginadas.map((empresa) => (
                    <TableRow
                      key={empresa.id}
                      sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                      hover
                    >
                      <TableCell component="th" scope="row">
                        {normalizarNomeEmpresa(empresa.nome)}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {empresa.cnpj.length === 11
                          ? empresa.cnpj.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4")
                          : normalizarCNPJ(empresa.cnpj)}
                      </TableCell>
                      <TableCell>{normalizarEmail(empresa.email)}</TableCell>
                      <TableCell>
                        {empresa.telefone
                          ? normalizarTelefone(empresa.telefone)
                          : "-"}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={empresa.ativo === "S" ? "Ativo" : "Inativo"}
                          color={empresa.ativo === "S" ? "success" : "error"}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          color={empresa.ativo === "S" ? "warning" : "success"}
                          onClick={() => abrirModalConfirmacao(empresa)}
                          title={
                            empresa.ativo === "S"
                              ? "Inativar empresa"
                              : "Ativar empresa"
                          }
                        >
                          {empresa.ativo === "S" ? (
                            <BlockIcon />
                          ) : (
                            <CheckCircleIcon />
                          )}
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          
          {/* Paginação para desktop */}
          <TablePagination
            component="div"
            count={filteredEmpresas.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
            labelRowsPerPage="Empresas por página:"
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`
            }
          />
        </Box>

        {/* Cards para mobile */}
        <Box sx={{ display: { xs: "block", md: "none" } }}>
          {empresasPaginadas.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography variant="body1" color="text.secondary">
                Nenhuma empresa encontrada
              </Typography>
            </Box>
          ) : (
            <Stack spacing={2}>
              {empresasPaginadas.map((empresa) => (
                <Paper
                  key={empresa.id}
                  elevation={2}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    "&:hover": {
                      elevation: 4,
                      backgroundColor: "action.hover",
                    },
                  }}
                >
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
                    {/* Info principal */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 600, mb: 0.5, fontSize: "1.1rem" }}
                      >
                        {normalizarNomeEmpresa(empresa.nome)}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 0.5 }}
                      >
                        {empresa.cnpj.length === 11 ? "CPF" : "CNPJ"}: {
                          empresa.cnpj.length === 11
                            ? empresa.cnpj.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4")
                            : normalizarCNPJ(empresa.cnpj)
                        }
                      </Typography>
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {normalizarEmail(empresa.email)}
                      </Typography>
                    </Box>

                    {/* Status */}
                    <Box sx={{ display: "flex", alignItems: "flex-start", ml: 2 }}>
                      <Chip
                        label={empresa.ativo === "S" ? "Ativo" : "Inativo"}
                        color={empresa.ativo === "S" ? "success" : "error"}
                        size="small"
                      />
                    </Box>
                  </Box>

                  {/* Informações adicionais */}
                  {(empresa.telefone) && (
                    <Box
                      sx={{
                        mb: 2,
                        pt: 1,
                        borderTop: 1,
                        borderColor: "divider",
                      }}
                    >
                      {empresa.telefone && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 0.5 }}
                        >
                          📞 {normalizarTelefone(empresa.telefone)}
                        </Typography>
                      )}
                    </Box>
                  )}

                  {/* Ações */}
                  <Box
                    sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}
                  >
                    <IconButton
                      size="small"
                      onClick={() => abrirModalConfirmacao(empresa)}
                      sx={{
                        bgcolor:
                          empresa.ativo === "S"
                            ? "warning.main"
                            : "success.main",
                        color: "white",
                        "&:hover": {
                          bgcolor:
                            empresa.ativo === "S"
                              ? "warning.dark"
                              : "success.dark",
                        },
                      }}
                      title={
                        empresa.ativo === "S"
                          ? "Inativar empresa"
                          : "Ativar empresa"
                      }
                    >
                      {empresa.ativo === "S" ? (
                        <BlockIcon fontSize="small" />
                      ) : (
                        <CheckCircleIcon fontSize="small" />
                      )}
                    </IconButton>
                  </Box>
                </Paper>
              ))}
            </Stack>
          )}
          
          {/* Paginação para mobile */}
          <TablePagination
            component="div"
            count={filteredEmpresas.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
            labelRowsPerPage="Empresas por página:"
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`
            }
            sx={{ mt: 2 }}
          />
        </Box>

        <Box
          mt={3}
          display="flex"
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          flexDirection={{ xs: "column", sm: "row" }}
          gap={{ xs: 2, sm: 0 }}
        >
          <Box>
            <Typography variant="body2" color="text.secondary">
              {showInactive
                ? `Empresas inativas: ${filteredEmpresas.length}`
                : `Empresas ativas: ${filteredEmpresas.length}`}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Total cadastradas: {empresas.length} (
              {empresas.filter((e) => e.ativo === "S").length} ativas,{" "}
              {empresas.filter((e) => e.ativo === "N").length} inativas)
            </Typography>
          </Box>
          <Button
            variant="outlined"
            onClick={fetchEmpresas}
            sx={{ width: { xs: "100%", sm: "auto" }, order: { xs: -1, sm: 0 } }}
          >
            Atualizar Lista
          </Button>
        </Box>
      </Paper>

      {/* Modal de Confirmação */}
      <Modal open={modalOpen} onClose={fecharModal} closeAfterTransition>
        <Fade in={modalOpen}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: { xs: "90%", sm: 500 },
              bgcolor: "background.paper",
              borderRadius: 2,
              boxShadow: 24,
              p: 4,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <WarningIcon
                sx={{
                  fontSize: 40,
                  color: acaoModal === "inativar" ? "warning.main" : "success.main",
                  mr: 2,
                }}
              />
              <Typography variant="h6" component="h2">
                Confirmar {acaoModal === "inativar" ? "Inativação" : "Ativação"}
              </Typography>
            </Box>

            <Typography variant="body1" sx={{ mb: 2 }}>
              Tem certeza que deseja {acaoModal} a empresa{" "}
              <strong>"{empresaParaAlterar?.nome}"</strong>?
            </Typography>

            {acaoModal === "inativar" && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <strong>ATENÇÃO:</strong> Todas as vagas desta empresa serão
                ocultadas do painel de vagas e não estarão mais disponíveis para
                candidaturas.
              </Alert>
            )}

            {acaoModal === "ativar" && (
              <Alert severity="info" sx={{ mb: 2 }}>
                As vagas desta empresa voltarão a ser exibidas no painel de vagas.
              </Alert>
            )}

            <Box
              sx={{
                display: "flex",
                gap: 2,
                justifyContent: "flex-end",
                mt: 3,
              }}
            >
              <Button variant="outlined" onClick={fecharModal}>
                Cancelar
              </Button>
              <Button
                variant="contained"
                color={acaoModal === "inativar" ? "warning" : "success"}
                onClick={confirmarAlteracao}
                startIcon={
                  acaoModal === "inativar" ? <BlockIcon /> : <CheckCircleIcon />
                }
              >
                {acaoModal === "inativar" ? "Inativar Empresa" : "Ativar Empresa"}
              </Button>
            </Box>
          </Box>
        </Fade>
      </Modal>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ListarEmpresas;
